/**
 * `/admin/users` — the user list (SPEC §9.1).
 *
 * The departments column is what the user's *grants* name, not what they can
 * reach: an administrator holds no grants and reaches everything, and a
 * `selected` grant can name a report that has gone away. The resolved answer
 * is one click away on the detail page, which is the only place it is shown,
 * because showing an approximation next to the real thing is how an
 * approximation gets trusted.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Pencil, ShieldCheck, Trash2, UserPlus, Users, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { Badge, Button, Card, Grid, TextField } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { adminApi } from '../../lib/admin'
import { ApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import type { AdminUserSummary } from '../../lib/adminTypes'

interface DepartmentChoice {
  readonly key: string
  readonly name: string
}

function DepartmentChecks({
  departments,
  selected,
  onChange,
  disabled = false,
}: {
  departments: readonly DepartmentChoice[]
  selected: readonly string[]
  onChange: (keys: readonly string[]) => void
  disabled?: boolean
}) {
  const held = new Set(selected)
  return (
    <fieldset className="space-y-1">
      <legend className="text-[length:var(--text-xs)] font-medium">Department granted</legend>
      <div className="flex flex-wrap gap-2">
        {departments.length === 0 ? (
          <span className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
            No active departments
          </span>
        ) : (
          departments.map((department) => (
            <label
              key={department.key}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-rule)] px-2 py-1 text-[length:var(--text-xs)]"
            >
              <input
                type="checkbox"
                checked={held.has(department.key)}
                disabled={disabled}
                onChange={() => {
                  const next = held.has(department.key)
                    ? selected.filter((key) => key !== department.key)
                    : [...selected, department.key]
                  onChange(next)
                }}
              />
              {department.name}
            </label>
          ))
        )}
      </div>
    </fieldset>
  )
}

function AddAccessForm({
  mode,
  departments,
  onCreated,
}: {
  mode: 'user' | 'admin'
  departments: readonly DepartmentChoice[]
  onCreated: () => void
}) {
  const [upn, setUpn] = useState('')
  const [name, setName] = useState('')
  const [departmentKeys, setDepartmentKeys] = useState<readonly string[]>([])
  const create = useMutation({
    mutationFn: () =>
      adminApi.createUser({
        upn,
        display_name: name.trim() === '' ? null : name,
        is_admin: mode === 'admin',
        department_keys: mode === 'admin' ? [] : departmentKeys,
      }),
    onSuccess: () => {
      setUpn('')
      setName('')
      setDepartmentKeys([])
      onCreated()
    },
  })
  const fields = create.error instanceof ApiError ? create.error.fields : {}

  return (
    <form
      className="rounded-[var(--radius-lg)] border border-[var(--color-rule)] p-3"
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate()
      }}
    >
      <div className="mb-3 flex items-center gap-2 font-medium">
        {mode === 'admin' ? (
          <ShieldCheck aria-hidden className="size-4" />
        ) : (
          <UserPlus aria-hidden className="size-4" />
        )}
        Add {mode}
      </div>
      <div className="grid gap-2">
        <TextField
          label="Sign-in name"
          placeholder={`${mode}@company.com`}
          value={upn}
          required
          onChange={(event) => {
            setUpn(event.target.value)
          }}
          error={fields.upn}
        />
        <TextField
          label="Name"
          placeholder="Optional"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
          error={fields.display_name}
        />
        {mode === 'user' && (
          <DepartmentChecks
            departments={departments}
            selected={departmentKeys}
            onChange={setDepartmentKeys}
          />
        )}
      </div>
      <Button className="mt-3" variant={mode === 'user' ? 'primary' : 'secondary'} type="submit">
        {create.isPending ? 'Adding...' : `Add ${mode}`}
      </Button>
      {create.error !== null && Object.keys(fields).length === 0 && (
        <div className="mt-3">
          <ErrorState error={create.error} />
        </div>
      )}
    </form>
  )
}

function UserAccessCard({
  departments,
  onChanged,
}: {
  departments: readonly DepartmentChoice[]
  onChanged: () => void
}) {
  return (
    <Card
      title="User access"
      description="Create portal users before first sign-in, or add administrators with full access."
    >
      <div className="mb-3 flex items-center gap-2 text-[length:var(--text-sm)] font-medium">
        <Users aria-hidden className="size-4" />
        Access setup
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <AddAccessForm mode="user" departments={departments} onCreated={onChanged} />
        <AddAccessForm mode="admin" departments={departments} onCreated={onChanged} />
      </div>
    </Card>
  )
}

function UserDepartmentAccessCell({
  user,
  departments,
  onSaved,
}: {
  user: AdminUserSummary
  departments: readonly DepartmentChoice[]
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [departmentKeys, setDepartmentKeys] = useState<readonly string[]>(
    user.granted_department_keys,
  )
  const save = useMutation({
    mutationFn: () => adminApi.setUserDepartmentAccess(user.id, departmentKeys),
    onSuccess: () => {
      setEditing(false)
      onSaved()
    },
  })

  if (user.is_admin) return <span>All (administrator)</span>

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span>{user.granted_department_keys.join(', ') || '-'}</span>
        <Button
          aria-label={`Edit department grants for ${user.display_name}`}
          title="Edit department grants"
          onClick={() => {
            setDepartmentKeys(user.granted_department_keys)
            setEditing(true)
          }}
        >
          <Pencil aria-hidden className="size-3.5" />
          Edit
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <DepartmentChecks
        departments={departments}
        selected={departmentKeys}
        disabled={save.isPending}
        onChange={setDepartmentKeys}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={save.isPending}
          onClick={() => {
            save.mutate()
          }}
        >
          <Check aria-hidden className="size-3.5" />
          {save.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button
          disabled={save.isPending}
          onClick={() => {
            setDepartmentKeys(user.granted_department_keys)
            setEditing(false)
          }}
        >
          <X aria-hidden className="size-3.5" />
          Cancel
        </Button>
      </div>
      {save.error !== null && <ErrorState error={save.error} />}
    </div>
  )
}

function DeleteUserButton({ user, onDeleted }: { user: AdminUserSummary; onDeleted: () => void }) {
  const remove = useMutation({
    mutationFn: () => adminApi.deleteUser(user.id),
    onSuccess: onDeleted,
  })

  return (
    <div className="space-y-1">
      <Button
        variant="danger"
        disabled={remove.isPending}
        onClick={() => {
          if (window.confirm(`Delete ${user.display_name}? This cannot be undone.`)) {
            remove.mutate()
          }
        }}
      >
        <Trash2 aria-hidden className="size-3.5" />
        {remove.isPending ? 'Deleting...' : 'Delete'}
      </Button>
      {remove.error !== null && <ErrorState error={remove.error} />}
    </div>
  )
}

export function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const q = searchParams.get('q') ?? ''
  const department = searchParams.get('department') ?? ''
  const [draft, setDraft] = useState(q)

  const departments = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: adminApi.departments,
  })
  const users = useQuery({
    queryKey: ['admin', 'users', q, department],
    queryFn: () => adminApi.users({ q: q || undefined, department: department || undefined }),
  })

  function refreshUsers(): void {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    void queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] })
    void queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  const activeDepartments = (departments.data ?? []).filter((item) => item.is_active)

  function setFilter(key: string, value: string): void {
    const next = new URLSearchParams(searchParams)
    if (value === '') next.delete(key)
    else next.set(key, value)
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="space-y-4">
      <UserAccessCard departments={activeDepartments} onChanged={refreshUsers} />

      <Card title="Users" description="Everyone who has signed in or has been added by an admin.">
      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setFilter('q', draft)
        }}
      >
        <div className="w-64">
          <TextField
            label="Search"
            placeholder="Name, sign-in name, or email"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
            }}
          />
        </div>
        <label className="text-[length:var(--text-xs)] font-medium">
          <span className="block">Department granted</span>
          <select
            value={department}
            onChange={(event) => {
              setFilter('department', event.target.value)
            }}
            className="mt-1 rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 py-1.5 text-[length:var(--text-sm)]"
          >
            <option value="">All</option>
            {activeDepartments.map((item) => (
              <option key={item.key} value={item.key}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </form>

      {users.isPending ? (
        <Skeleton rows={4} />
      ) : users.error !== null ? (
        <ErrorState
          error={users.error}
          onRetry={() => {
            void users.refetch()
          }}
        />
      ) : (
        <Grid head={['User', 'Roles', 'Departments (granted)', 'Last sign-in', 'Status', 'Actions']}>
          {users.data.map((user) => (
            <tr key={user.id} className="border-b border-[var(--color-rule)] last:border-0">
              <td className="px-2 py-1.5">
                <Link
                  to={`/admin/users/${user.id}`}
                  className="font-medium hover:text-[var(--accent)]"
                >
                  {user.display_name}
                </Link>
                <div className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                  {user.upn}
                </div>
              </td>
              <td className="px-2 py-1.5">
                <span className="flex flex-wrap gap-1">
                  {user.role_keys.length === 0 ? (
                    <span className="text-[var(--color-ink-muted)]">—</span>
                  ) : (
                    user.role_keys.map((key) => <Badge key={key}>{key}</Badge>)
                  )}
                </span>
              </td>
              <td className="px-2 py-1.5 text-[length:var(--text-xs)]">
                <UserDepartmentAccessCell
                  user={user}
                  departments={activeDepartments}
                  onSaved={refreshUsers}
                />
              </td>
              <td className="numeric px-2 py-1.5 text-[length:var(--text-xs)]">
                {user.last_login_at === null ? '—' : formatDateTime(user.last_login_at)}
              </td>
              <td className="px-2 py-1.5">
                <span className="flex gap-1">
                  {user.is_admin && <Badge tone="positive">admin</Badge>}
                  {!user.is_active && <Badge tone="danger">deactivated</Badge>}
                </span>
              </td>
              <td className="px-2 py-1.5">
                <DeleteUserButton user={user} onDeleted={refreshUsers} />
              </td>
            </tr>
          ))}
        </Grid>
      )}
      </Card>
    </div>
  )
}
